"use client";

import { useEffect, useRef } from "react";

export interface UseUnsavedChangesGuardOptions {
  enabled: boolean;
  message: string;
  confirmLeave?: (message: string) => boolean;
}

function isModifiedClick(event: MouseEvent): boolean {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  );
}

function isSamePageLink(anchor: HTMLAnchorElement): boolean {
  return (
    anchor.origin === window.location.origin &&
    anchor.pathname === window.location.pathname &&
    anchor.search === window.location.search
  );
}

export function useUnsavedChangesGuard({
  enabled,
  message,
  confirmLeave = (value) => window.confirm(value),
}: UseUnsavedChangesGuardOptions): void {
  const messageRef = useRef(message);
  const confirmLeaveRef = useRef(confirmLeave);
  messageRef.current = message;
  confirmLeaveRef.current = confirmLeave;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || isModifiedClick(event)) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }
      if (isSamePageLink(anchor)) {
        return;
      }

      if (!confirmLeaveRef.current(messageRef.current)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [enabled]);
}
